import { z } from 'zod';

import { siteAdminSpecSchema } from '../access/site-admin';

/**
 * Die Einrichtung einer Anwendung nach dem ersten Start — als **Aufzählung**,
 * niemals als Kommandozeile.
 *
 * ## Die Regel, um die es hier geht
 *
 * „Der Agent nimmt Spezifikationen entgegen, niemals Befehle." Er hat Zugriff
 * auf den Docker-Socket, und der ist gleichbedeutend mit Root auf dem Server.
 * `docs/architecture.md` §6 skizzierte für diesen Meilenstein ein Manifest mit
 * einem freien `command: ['wp', 'core', 'install', …]`. Genau das ist die
 * Kommandozeile, die das Sicherheitsmodell ausschließt: Ein fehlerhafter oder
 * übernommener Master könnte damit auf jedem Worker beliebigen Code ausführen.
 *
 * ## Was stattdessen übertragen wird
 *
 * Ein Wert aus dieser Aufzählung und die Daten, die dazugehören. **Die
 * Argumentliste bildet der Agent selbst**, aus eigenen Konstanten
 * (`stacks/post-install.ts` im Worker). Vorbild ist `perform_restart` in
 * `infra/kraaapf-sync`: Der Master benennt ein Ziel, die Bedeutung des Ziels
 * steht ausschließlich auf der ausführenden Seite. Ein unbekannter Wert wird
 * abgelehnt und nicht etwa durchgereicht.
 *
 * Was der Master beisteuert, sind ausschließlich **Werte** — Benutzername,
 * E-Mail, Passwort, Adresse der Site. Sie stehen als `--schalter=wert` in
 * jeweils *einem* Argument, damit ein Wert nicht als eigener Schalter gelesen
 * werden kann, und ihr Zeichenvorrat ist im Vertrag eng gefasst
 * (`access/site-admin.ts`). Ausgeführt wird in einem **Einmal-Container** aus
 * dem Image, das der Blueprint für diesen Dienst nennt — es gibt kein
 * `docker exec` und keinen zweiten Weg an den Socket.
 *
 * ## Der Preis, ehrlich benannt
 *
 * Ein Website-Typ, der eine Einrichtung braucht, ist damit **nicht mehr bloß
 * eine YAML-Datei**: Ein neuer Wert in dieser Aufzählung ist eine Änderung am
 * Agent und ein Rollout der Flotte. Das ist der bewusst gewählte Tausch — die
 * Alternative wäre ein Weg, auf dem eine Zeichenkette des Masters zu einer
 * Kommandozeile auf einem Server mit Root-äquivalentem Docker-Zugriff wird.
 */

/**
 * Alle Einrichtungshandlungen, die der Agent kennt.
 *
 * Diese Liste ist die Grenze. Sie zu erweitern heißt, dem Agent eine neue
 * Fähigkeit zu geben — und genau deshalb steht sie hier und nicht in einem
 * Manifest, das jeder anlegen kann.
 */
export const POST_INSTALL_ACTIONS = ['wordpress-core-install'] as const;

export type PostInstallAction = (typeof POST_INSTALL_ACTIONS)[number];

/**
 * Wohin sich der Betreiber nach der Einrichtung anmeldet.
 *
 * Steht im Vertrag und nicht im Master, weil beide Seiten dieselbe Antwort
 * brauchen: Der Master gibt sie in den Zugangsdaten aus, und ein späterer
 * Erreichbarkeitstest fragt dieselbe Adresse ab. Zwei Kopien wären zwei
 * Gelegenheiten, sie auseinanderlaufen zu lassen.
 */
export const POST_INSTALL_LOGIN_PATHS: Record<PostInstallAction, string> = {
  'wordpress-core-install': '/wp-admin/',
};

/**
 * Die Einrichtungshandlung eines Dienstes, wie sie in der `SiteSpec` steht.
 *
 * Die Zugangsdaten hängen ausdrücklich **an der Handlung** und nicht an der
 * Site: Eine Site ohne Einrichtung hat keinen Administrator, und ein Feld auf
 * der Site trüge dann dauerhaft `null` — ein Zustand, den niemand mehr liest
 * und den man deshalb auch nicht mehr prüft.
 */
export const postInstallSchema = z.object({
  kind: z.enum(POST_INSTALL_ACTIONS),
  admin: siteAdminSpecSchema,
});

export type PostInstall = z.infer<typeof postInstallSchema>;
